import { useId, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { CopyIcon } from "@/components/ui/icons/CopyIcon";
import { cn } from "@/lib/cn";

const FAILED_MESSAGE =
  "Couldn't copy automatically. The value is selected; press Ctrl+C to copy it.";

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7.5L5.5 10.5L11.5 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CopyableValueProps {
  label: string;
  value: string;
  hint?: string;
  /** The quieter label of a field inside a nested block, such as one IdP connection */
  compact?: boolean;
  /** A short code a person reads out or types by hand, such as a support code, set large */
  prominent?: boolean;
  /** Preserve line breaks in diagnostic summaries while allowing long values to wrap. */
  multiline?: boolean;
}

/** A value an admin pastes into another system, such as their identity provider. */
export const CopyableValue = ({
  label,
  value,
  hint,
  compact = false,
  prominent = false,
  multiline = false,
}: CopyableValueProps) => {
  const labelId = useId();
  const valueRef = useRef<HTMLElement>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const selectValue = () => {
    const node = valueRef.current;
    const selection = window.getSelection();
    if (!node || !selection) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleCopy = async () => {
    try {
      // navigator.clipboard is undefined on insecure (http) origins, e.g. local dev
      if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(value);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 3000);
    } catch {
      selectValue();
      setCopyState("failed");
    }
  };

  return (
    <div className="space-y-2">
      <p
        id={labelId}
        className={cn(
          "font-medium leading-none",
          compact ? "text-xs text-muted-foreground" : "text-sm",
        )}
      >
        {label}
      </p>
      {/* Below sm the value takes the full width above the button, so a long URL is not squeezed
          into a few characters per line */}
      <div
        className={cn(
          "flex flex-col gap-2 sm:flex-row",
          prominent ? "items-stretch" : "items-start",
        )}
      >
        <code
          ref={valueRef}
          aria-labelledby={labelId}
          className={cn(
            "w-full min-w-0 break-all rounded-sm bg-muted font-mono sm:w-auto sm:flex-1",
            multiline && "whitespace-pre-wrap",
            prominent
              ? "select-all px-4 py-3 text-center text-2xl font-semibold tracking-widest"
              : "px-3 py-2 text-sm",
          )}
        >
          {value}
        </code>
        {/* The fixed minimum width keeps "Copied" from shifting the value; beside the value it is
            as tall as a one-line value's box, so it lines up with it, and with the first line of a
            value that wraps */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn("min-w-26 shrink-0", prominent ? "sm:h-auto" : "sm:h-9")}
          aria-label={`Copy ${label}`}
          onClick={handleCopy}
        >
          {copyState === "copied" ? (
            <>
              <CheckIcon />
              Copied
            </>
          ) : (
            <>
              <CopyIcon color="currentColor" />
              Copy
            </>
          )}
        </Button>
      </div>
      {/* The button keeps its name, so the outcome is announced from a region that is always there */}
      <span role="status" className="sr-only">
        {copyState === "copied" && `${label} copied`}
        {copyState === "failed" && FAILED_MESSAGE}
      </span>
      {copyState === "failed" ? (
        <p className="text-xs text-destructive-fg">{FAILED_MESSAGE}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
};
